import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/site";
import PageHero from "../components/site/PageHero";
import { NewsletterForm, Overline } from "../components/site/Primitives";

const EASE = [0.16, 1, 0.3, 1];

const fmt = (d) => {
  try { return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-page">
      <PageHero overline="The Journal" title="Education, business & beauty." subtitle="Guides, tips, and insights for aspiring nail professionals, salon owners, and luxury self-care lovers." />

      <section className="bg-brand-cream py-24 md:py-32" data-testid="blog-grid">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          {featured && (
            <motion.article initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }} data-testid={`blog-featured-${featured.id}`} className="grid lg:grid-cols-2 gap-8 items-center bg-brand-blush rounded-[2rem] overflow-hidden mb-14">
              <div className="relative h-72 lg:h-[440px] overflow-hidden group">
                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
              </div>
              <div className="p-9 md:p-12">
                <span className="text-[11px] uppercase tracking-[0.24em] text-brand-rose">{featured.category} · {fmt(featured.date)}</span>
                <h2 className="font-serif font-light text-brand-ink text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05]">{featured.title}</h2>
                <p className="text-brand-ink/70 font-light mt-5 leading-relaxed">{featured.excerpt}</p>
                <button data-testid="blog-featured-read" className="mt-7 inline-flex items-center gap-2 text-sm text-brand-ink border-b border-brand-gold pb-0.5 hover:text-brand-rose transition-colors">Read article <ArrowUpRight size={16} /></button>
              </div>
            </motion.article>
          )}

          <div className="grid gap-8 md:grid-cols-3">
            {rest.map((p, i) => (
              <motion.article key={p.id} data-testid={`blog-card-${p.id}`} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, ease: EASE, delay: i * 0.1 }} className="group flex flex-col">
                <div className="relative h-60 overflow-hidden rounded-3xl">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <span className="text-[11px] uppercase tracking-[0.24em] text-brand-rose mt-5">{p.category} · {fmt(p.date)}</span>
                <h3 className="font-serif text-2xl text-brand-ink tracking-tight mt-2">{p.title}</h3>
                <p className="text-brand-ink/70 font-light mt-3 text-sm leading-relaxed flex-1">{p.excerpt}</p>
                <button data-testid={`blog-read-${p.id}`} className="mt-5 inline-flex items-center gap-2 text-sm text-brand-ink border-b border-brand-gold pb-0.5 self-start hover:text-brand-rose transition-colors">Read more <ArrowUpRight size={15} /></button>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-pink/50 py-20 md:py-28" data-testid="blog-newsletter">
        <div className="max-w-2xl mx-auto px-6 text-center flex flex-col items-center">
          <Overline className="mb-5">Never miss a post</Overline>
          <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl tracking-tight">Get our latest straight to your inbox.</h2>
          <div className="mt-8 w-full flex justify-center"><NewsletterForm testId="blog-newsletter-form" source="blog" /></div>
        </div>
      </section>
    </div>
  );
}
